import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  writeBatch,
  arrayRemove,
  serverTimestamp,
} from 'firebase/firestore';
import { getAuth, signInWithEmailAndPassword, deleteUser } from 'firebase/auth';
import { getCloudFunction } from '../../utilities/cloudFunctions';
import { db } from '../../firebase/firebase';
import { IMember } from '../../types/organisation';
import { hasRole } from '../../utilities/roleUtils';
import { isDemoMode } from '../../utilities/demoMode';
import { DemoDataManager } from '../../services/demoDataManager';

/**
 * Get all members of an organisation
 */
export async function getOrganisationMembers(orgId: string): Promise<IMember[]> {
  try {
    // Check if in demo mode and organisational tutorial is active
    if (isDemoMode()) {
      const tutorialFlow = localStorage.getItem('demo_tutorial_flow');
      if (tutorialFlow === 'organisational-projects-tutorial') {
        const demoOrg = DemoDataManager.getDemoOrganisation();
        if (demoOrg && demoOrg.id === orgId) {
          return DemoDataManager.getDemoMembers();
        }
      }
    }

    // Get regular members
    const usersRef = collection(db, 'Users');
    const q = query(usersRef, where(`Organisations.${orgId}`, '!=', null));
    const querySnapshot = await getDocs(q);
    
    const members = querySnapshot.docs.map(doc => {
      const userData = doc.data();
      const orgData = userData.Organisations[orgId];
      return {
        ...orgData,
        userKey: doc.id,
        name: orgData.name || userData.userProfile?.userName || userData.email || 'Unnamed Member',
        isTemporary: false, // Explicitly set to false for regular members
        roles: orgData.roles || [orgData.role || 'member'] // Handle both new and legacy data
      };
    });

    // Get temporary users
    const tempQuery = query(
      collection(db, 'Users'),
      where('isTemporary', '==', true),
      where('organisationId', '==', orgId)
    );
    const tempSnapshot = await getDocs(tempQuery);
    
    const tempMembers = tempSnapshot.docs.map(doc => {
      const userData = doc.data();
      return {
        userKey: doc.id,
        organisationId: orgId,
        roles: ['member'],
        name: userData.userProfile?.userName || 'Unnamed Student',
        joinedAt: userData.createdAt,
        groupIds: userData.groupIds || [],
        animalEmoji: userData.userProfile?.userName?.split(' ')[0] || '🐼',
        isTemporary: true,
        tempUserId: doc.id,
        registered: false
      } as IMember;
    });

    // Create a Map to deduplicate members
    const memberMap = new Map();
    [...members, ...tempMembers].forEach(member => {
      const id = member.userKey || member.tempUserId;
      if (id) {
        memberMap.set(id, member);
      }
    });

    return Array.from(memberMap.values());
  } catch (error) {
    console.error('Error getting Organisation members:', error);
    throw error;
  }
}

/**
 * Remove a member from an organisation
 */
export async function removeMember(orgId: string, memberId: string): Promise<void> {
  try {
    const userRef = doc(db, 'Users', memberId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      throw new Error('User not found');
    }

    const userData = userDoc.data();
    const batch = writeBatch(db);

    // Handle temporary user deletion
    if (userData.isTemporary) {
      const groupIds: string[] = userData.groupIds || [];

      // Strip member from groups in the same batch as user/code deletes
      for (const groupId of groupIds) {
        batch.update(doc(db, 'groups', groupId), {
          memberIds: arrayRemove(memberId),
          updatedAt: serverTimestamp(),
        });
      }

      // Delete the generated code from the organisation's generatedCodes collection
      const generatedCodesRef = collection(db, 'Organisations', orgId, 'generatedCodes');
      const generatedCodesQuery = query(generatedCodesRef, where('tempUserId', '==', memberId));
      const generatedCodesSnapshot = await getDocs(generatedCodesQuery);

      generatedCodesSnapshot.docs.forEach(codeDoc => {
        batch.delete(codeDoc.ref);
      });

      // Delete any access codes associated with this temporary user
      const accessCodesRef = collection(db, 'accessCodes');
      const accessCodesQuery = query(accessCodesRef, where('tempUserId', '==', memberId));
      const accessCodesSnapshot = await getDocs(accessCodesQuery);

      accessCodesSnapshot.docs.forEach(codeDoc => {
        batch.delete(codeDoc.ref);
      });

      batch.delete(userRef);
      await batch.commit();
      return;
    }

    // Handle regular user (admin, moderator, etc.)
    const updatedOrganisations = { ...userData.Organisations };
    const memberData = updatedOrganisations[orgId];
    
    if (memberData) {
      // Get the access code from the member data
      const accessCode = memberData.accessCode;

      // If there's an access code, delete it from the accessCodes collection
      if (accessCode) {
        const accessCodeRef = doc(db, 'accessCodes', accessCode);
        batch.delete(accessCodeRef);

        // Also delete from the organisation's generatedCodes subcollection
        const generatedCodeRef = doc(db, 'Organisations', orgId, 'generatedCodes', accessCode);
        batch.delete(generatedCodeRef);
      }

      // Remove user from all groups in the same batch
      const groupIds: string[] = memberData.groupIds || [];
      for (const groupId of groupIds) {
        batch.update(doc(db, 'groups', groupId), {
          memberIds: arrayRemove(memberId),
          updatedAt: serverTimestamp(),
        });
      }
    }

    // Delete any generated codes associated with this user
    const generatedCodesRef = collection(db, 'Organisations', orgId, 'generatedCodes');
    const generatedCodesQuery = query(generatedCodesRef, where('userKey', '==', memberId));
    const generatedCodesSnapshot = await getDocs(generatedCodesQuery);
    generatedCodesSnapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });

    // Only remove the organisation from the user's Organisations map
    delete updatedOrganisations[orgId];
    
    // Delete the entire user document if:
    // 1. This was their last organisation, OR
    // 2. They were created by a moderator/admin in this org, OR
    // 3. They are a regular member (not an admin or moderator in other orgs)
    const isAdmin = Object.values(updatedOrganisations).some((org: any) => hasRole(org, 'organisationAdmin'));
    const isModerator = Object.values(updatedOrganisations).some((org: any) => hasRole(org, 'moderator'));
    const shouldDeleteUser = 
      Object.keys(updatedOrganisations).length === 0 || 
      (userData.moderatorEmail && userData.email?.startsWith(userData.moderatorEmail.split('@')[0])) ||
      (!isAdmin && !isModerator); // Only delete if they're not an admin/moderator in other orgs

    if (shouldDeleteUser) {
      batch.delete(userRef);
      // Delete the user from Firebase Auth if they were created by a moderator
      if (userData.moderatorEmail && userData.email) {
        try {
          const auth = getAuth();
          // First sign in as the user to get their auth record
          const userCredential = await signInWithEmailAndPassword(auth, userData.email, userData.accessCode || '');
          if (userCredential.user) {
            await deleteUser(userCredential.user);
          }
        } catch (error) {
          console.error('Error deleting user from auth:', error);
          // Continue with other deletions even if auth deletion fails
        }
      }
    } else {
      batch.update(userRef, {
        Organisations: updatedOrganisations
      });
    }

    // Handle access codes
    if (userData.email) {
      // Find and delete any access codes for this user in this Organisation
      const accessCodesRef = collection(db, 'accessCodes');
      const accessCodesQuery = query(
        accessCodesRef,
        where('organisationId', '==', orgId),
        where('email', '==', userData.email)
      );
      const accessCodesSnapshot = await getDocs(accessCodesQuery);
      
      accessCodesSnapshot.docs.forEach(doc => {
        batch.delete(doc.ref);
      });
    }

    // Commit all changes
    await batch.commit();

  } catch (error) {
    console.error('Error removing member:', error);
    throw error;
  }
}

/**
 * Add a member to an organisation using an access code.
 * Membership writes go through joinOrganisationWithAccessCode (Admin SDK) so
 * clients cannot forge Users.Organisations roles.
 */
export async function addMemberWithAccessCode(
  accessCode: string,
  userId: string
): Promise<IMember> {
  try {
    const auth = getAuth();
    if (!auth.currentUser || auth.currentUser.uid !== userId) {
      throw new Error('You must be signed in as the joining user');
    }

    const joinFn = getCloudFunction<
      { accessCode: string },
      { success: boolean; organisationId: string; role: string }
    >('joinOrganisationWithAccessCode');

    const result = await joinFn({ accessCode });
    const { organisationId, role } = result.data;

    const userDoc = await getDoc(doc(db, 'Users', userId));
    if (!userDoc.exists()) {
      throw new Error('Membership was not created');
    }
    const membership = userDoc.data().Organisations?.[organisationId] as IMember | undefined;
    if (!membership) {
      throw new Error('Membership was not created');
    }
    return {
      ...membership,
      organisationId,
      roles: membership.roles?.length
        ? membership.roles
        : [role as IMember['roles'][number]],
    };
  } catch (error) {
    console.error('Error adding member with access code:', error);
    throw error;
  }
}

export interface UserLoginMembership {
  organisations: Record<string, unknown>;
  hasAuthorProfile: boolean;
}

/**
 * Organisation memberships plus whether the user has an artist/author profile.
 */
export async function getUserLoginMembership(userId: string): Promise<UserLoginMembership> {
  try {
    const userDoc = await getDoc(doc(db, 'Users', userId));
    if (!userDoc.exists()) {
      return { organisations: {}, hasAuthorProfile: false };
    }
    const data = userDoc.data();
    return {
      organisations: data.Organisations || {},
      hasAuthorProfile: Boolean(data.userProfile?.authorKey),
    };
  } catch (error) {
    console.error('Error getting user login membership:', error);
    throw error;
  }
}

/**
 * Get user's organisations
 */
export async function getUserOrganisations(userId: string) {
  const { organisations } = await getUserLoginMembership(userId);
  return organisations;
}
